<template>
  <div class="order-panel-list">
    <Spinner v-if="loading" />
    <p v-if="!userHasReadPermission(PermissionTypes.OrderPermissionTypes.order)">
      Payments not accessible
    </p>
    <div v-if="!loading && userHasReadPermission(PermissionTypes.OrderPermissionTypes.order)">
      <p class="text-danger font-weight-bold" v-if="loadingError">{{loadingError}}</p>

      <div v-if="!hasPayments">No payments yet reserved for this order</div>
      <div v-if="hasPayments">
        <p>Payments reserved for this order</p>
        <p>Invoices generated from this order will be allocated these payments reserved for the order</p>
        <ItemPanelList class="mt-2">
          <ItemPanel v-for="reservedPayment of linkedReservedPayments" :key="reservedPayment.id" icon="fa-usd-circle">
            <template v-slot:title>
              <ActionDropMenu class="float-right" buttonClass="btn-xxs" :forceDropdown="true">
                <DropdownMenuItem @click="sendPaymentReceipt(reservedPayment)" icon="fa-envelope" v-if="isPaymentReceiptSendable(reservedPayment)">Send Payment Receipt</DropdownMenuItem>
              </ActionDropMenu>
              <strong>
                <template v-if="isReservedPaymentOverpayment(reservedPayment)">
                  <LinkOverPayment :id="reservedPayment.overPayment.id" name="Direct Payment" />
                </template>
                <template v-if="isReservedPaymentCreditNote(reservedPayment)">
                  <LinkCreditNote :id="reservedPayment.creditNote.id" name="Credit Note" />
                </template>
              </strong>
            </template>
            <template v-slot:description>
              <span v-if="isReservedPaymentOverpayment(reservedPayment)">{{reservedPayment.overPayment.sequenceNumber}}</span>
              <span v-if="isReservedPaymentCreditNote(reservedPayment)">{{reservedPayment.creditNote.sequenceNumber}}</span>
            </template>
            <template v-slot:content>
              <template v-if="isReservedPaymentOverpayment(reservedPayment)">
                <ItemPanelData title="Overpayment Status">
                  <LabelBadge :labelClass="`label-payment-status-${reservedPayment.overPayment.status}`">{{reservedPayment.overPayment.status | formatLookupName(OverpaymentStatusTypeLookup)}}</LabelBadge>
                </ItemPanelData>
                <ItemPanelData title="Type">
                  <LabelBadge :labelClass="`label-payment-type-${reservedPayment.overPayment.paymentReceived.paymentType}`">{{reservedPayment.overPayment.paymentReceived.paymentType | formatLookupName(PaymentTypeLookup)}}</LabelBadge>
                </ItemPanelData>
                <ItemPanelData title="Date">
                  {{reservedPayment.overPayment.dateCreated | formatDateTime}}
                </ItemPanelData>
                <ItemPanelData title="Reserved Amount">
                  <Tooltip label="Amount of the payment reserved towards this order, pending invoice"><strong>{{reservedPayment.reservedAmount | formatCurrency}}</strong></Tooltip>
                </ItemPanelData>
                <ItemPanelData title="Total Amount">
                  <strong>{{reservedPayment.overPayment.amount | formatCurrency}}</strong>
                </ItemPanelData>
              </template>
              <template v-if="isReservedPaymentCreditNote(reservedPayment)">
                <ItemPanelData title="Credit Note Status">
                  <LabelBadge :labelClass="`label-creditnote-status-${reservedPayment.creditNote.status}`">{{reservedPayment.creditNote.status | formatLookupName(CreditNoteStatusTypeLookup)}}</LabelBadge>
                </ItemPanelData>
                <ItemPanelData title="Date">
                  {{reservedPayment.creditNote.createdAt | formatDateTime}}
                </ItemPanelData>
                <ItemPanelData title="Reserved Amount">
                  <Tooltip label="Amount of the credit note reserved towards this order, pending invoice"><strong>{{reservedPayment.reservedAmount | formatCurrency}}</strong></Tooltip>
                </ItemPanelData>
                <ItemPanelData title="Total Amount">
                  <strong>{{reservedPayment.creditNote.totalCredit | formatCurrency}}</strong>
                </ItemPanelData>
              </template>
            </template>
          </ItemPanel>
        </ItemPanelList>
      </div>
    </div>
  </div>
</template>

<script>
import FormMixin from '@/mixins/form'
import { PaymentStatusTypeLookup, PaymentTypeLookup, OverpaymentStatusTypes, OverpaymentStatusTypeLookup, OrderReservedPaymentTypes, OrderReservedPaymentStatusTypeLookup, CreditNoteStatusTypeLookup } from '@/store/types'

import SendPaymentReceivedDialog from '@/modules/accounts/views/sendPaymentReceivedDialog'

export default {
  name: 'OrderPaymentList',
  mixins: [FormMixin],
  props: {
    orderId: Number
  },
  data() {
    return {
      loading: false,
      loadingError: '',
      linkedReservedPayments: []
    }
  },
  watch: {
    orderId: function() {
      this.reloadData()
    }
  },
  computed: {
    hasPayments() {
      return this.linkedReservedPayments.length > 0
    }
  },
  created() {
    this.PaymentStatusTypeLookup = PaymentStatusTypeLookup
    this.PaymentTypeLookup = PaymentTypeLookup
    this.OverpaymentStatusTypeLookup = OverpaymentStatusTypeLookup
    this.OrderReservedPaymentStatusTypeLookup = OrderReservedPaymentStatusTypeLookup
    this.CreditNoteStatusTypeLookup = CreditNoteStatusTypeLookup
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      this.loadingError = ''

      this.loading = true
      this.$apiService.ordersService.getOrderPayments(this.orderId)
      .then(response => {
        this.linkedReservedPayments = response.data
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
      })
    },
    reloadData() {
      this.requestData()
    },
    sendPaymentReceipt(reservedPayment) {
      if(!reservedPayment.overPayment || !reservedPayment.overPayment.paymentReceived)
        return

      this.displayModal(SendPaymentReceivedDialog, { 
        paymentReceivedId: parseInt(reservedPayment.overPayment.paymentReceivedId),
        contactId: parseInt(reservedPayment.overPayment.paymentReceived.contactId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Receipt Sent')
          this.reloadData()
        }
      })
    },
    isPaymentReceiptSendable(reservedPayment) {
      if(reservedPayment.type == OrderReservedPaymentTypes.overpayment) {
        if(!this.userHasCreatePermission(this.PermissionTypes.AccountsPermissionTypes.orderEntryPayment))
          return false
        if(reservedPayment.overPayment.status == OverpaymentStatusTypes.cancelled)
          return false
        return true
      }

      return false
    },
    isReservedPaymentOverpayment(reservedPayment) {
      return reservedPayment.type == OrderReservedPaymentTypes.overpayment && reservedPayment.overPayment
    },
    isReservedPaymentCreditNote(reservedPayment) {
      return reservedPayment.type == OrderReservedPaymentTypes.creditNote && reservedPayment.creditNote
    }
  }


}
</script>